/**
 * Diálogo para nombrar un pedido — típicamente el cliente sentado en la
 * mesa ("Mesa 4 · Juan"). Dos caminos:
 *   - Nombre libre: cualquiera puede escribirlo, no crea registros.
 *   - Cliente registrado (permiso `customers.view`): vincula el pedido al
 *     cliente; si la venta termina a crédito queda preseleccionado.
 */

import { Button } from '@atreus/ui';
import { Search, UserRound, X } from 'lucide-react';
import { useEffect, useId, useState, type ReactElement } from 'react';

import { CustomerPicker } from '../customers/CustomerPicker.js';
import { usePermissions } from '../../lib/permissions.js';
import { trpc } from '../../lib/trpc.js';

export const GUEST_NAME_MAX = 80;

interface LinkedCustomer {
  id: string;
  fullName: string;
}

export const OrderGuestDialog = ({
  orderId,
  guestName,
  customer,
  onClose,
}: {
  orderId: string;
  guestName: string | null;
  customer: LinkedCustomer | null;
  onClose: () => void;
}): ReactElement => {
  const utils = trpc.useUtils();
  const permissions = usePermissions();
  const canLinkCustomer = permissions.canDo('customers.view');
  const inputId = useId();

  const [name, setName] = useState(guestName ?? '');
  const [linked, setLinked] = useState<LinkedCustomer | null>(customer);
  const [picking, setPicking] = useState(false);

  const save = trpc.orders.setGuest.useMutation({
    onSuccess: () => {
      void utils.orders.getById.invalidate({ id: orderId });
      void utils.orders.list.invalidate();
      onClose();
    },
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !picking) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, picking]);

  const hadName = guestName !== null || customer !== null;
  const submit = (): void => {
    save.mutate({
      orderId,
      customerId: linked?.id ?? null,
      guestName: linked ? null : name.trim() || null,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-fg/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="atreus-order-guest-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* div y no <form>: CustomerPicker trae su propio <form> de creación. */}
      <div className="w-full max-w-md rounded-lg border border-border bg-surface-raised shadow-xl">
        <header className="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <h3 id="atreus-order-guest-title" className="text-lg font-black tracking-tight">
              Nombre del pedido
            </h3>
            <p className="text-xs text-fg-muted">
              Identifica la mesa por el cliente, ej. &quot;Juan&quot; o &quot;Cumpleaños Ana&quot;.
            </p>
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={onClose} aria-label="Cerrar">
            <X className="size-4" />
          </Button>
        </header>

        <div className="space-y-3 px-5 py-4">
          {picking ? (
            <CustomerPicker
              onSelect={(c) => {
                setLinked({ id: c.id, fullName: c.fullName });
                setPicking(false);
              }}
              onCancel={() => setPicking(false)}
            />
          ) : linked ? (
            <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-surface px-3 py-2">
              <span className="flex min-w-0 items-center gap-2 text-sm">
                <UserRound className="size-4 shrink-0 text-fg-muted" />
                <span className="min-w-0">
                  <span className="block truncate font-semibold">{linked.fullName}</span>
                  <span className="block text-xs text-fg-muted">Cliente registrado</span>
                </span>
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setName(linked.fullName);
                  setLinked(null);
                }}
              >
                Desvincular
              </Button>
            </div>
          ) : (
            <>
              <div>
                <label htmlFor={inputId} className="block text-sm font-semibold tracking-tight">
                  Nombre
                </label>
                <input
                  id={inputId}
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      submit();
                    }
                  }}
                  maxLength={GUEST_NAME_MAX}
                  placeholder="Nombre del cliente"
                  className="mt-1 w-full rounded-md border border-border bg-surface px-3 py-2 text-base outline-none focus:border-fg"
                />
              </div>
              {canLinkCustomer ? (
                <Button type="button" variant="outline" size="sm" onClick={() => setPicking(true)}>
                  <Search className="size-3.5" /> Buscar cliente registrado
                </Button>
              ) : null}
            </>
          )}
          {save.error ? <p className="text-xs text-danger">{save.error.message}</p> : null}
        </div>

        {picking ? null : (
          <footer className="flex items-center justify-between gap-2 border-t border-border px-5 py-3">
            {hadName ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={save.isPending}
                onClick={() => save.mutate({ orderId, customerId: null, guestName: null })}
              >
                Quitar nombre
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button type="button" variant="ghost" size="md" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="button" size="md" onClick={submit} disabled={save.isPending}>
                {save.isPending ? 'Guardando…' : 'Guardar'}
              </Button>
            </div>
          </footer>
        )}
      </div>
    </div>
  );
};
